/**
 * Apple and Google, for any page that draws `SignInChoices` (GH-856, GH-859, GH-865).
 *
 * Lifted out of `DonorPanel` when `/c/<eventId>` gained the same two doors: the alternative was a
 * second copy of the popup-before-await ordering, the nonce round trip and the cancel rules, and
 * two copies of a sign-in flow is how one of them quietly stops working. The host still owns its
 * own journey — what happens once a session exists — through `onIssued`.
 *
 * ## What decides whether a door is drawn
 *
 * Only the server's sign-in options for this origin: a provider with no return address registered
 * here would open a window that cannot come back. Both are offered on every browser — the owner
 * lifted GH-856's "Apple on Apple devices only" rule on 2026-10-03 (GH-866), so a donor on Windows
 * or Android sees Apple and Google side by side, as on an iPhone.
 */
import { useEffect, useRef, useState } from "react";
import type { DonorSessionResponse, DonorSignInOptions } from "@givehere/contracts";
import { appleReturnAddress, authorizeWithApple } from "./apple-sign-in.js";
import {
  authorizeWithGoogle,
  GoogleSignInCancelled,
  googleReturnAddress,
  openGoogleWindow,
} from "./google-sign-in.js";
import {
  confirmAppleWebSignIn,
  confirmGoogleWebSignIn,
  DonorApiError,
  fetchSignInOptions,
  rememberToken,
  startAppleWebSignIn,
  startGoogleWebSignIn,
  type SignUpAccess,
} from "./donor-session.js";
import { accessFields, isSignUpRefusal, type DoorAttempt } from "./sign-up-access.js";

export type IssuedHandler = (issued: DonorSessionResponse) => Promise<void> | void;

export type ProviderSignIn = {
  readonly appleOffered: boolean;
  readonly googleOffered: boolean;
  /** Apple's sheet, then the session. `onIssued` runs after the device is remembered. */
  readonly signInWithApple: (onIssued: IssuedHandler) => Promise<void>;
  /** Must be called inside the tap itself: it opens Google's window before anything awaits. */
  readonly signInWithGoogle: (onIssued: IssuedHandler) => Promise<void>;
};

export function useProviderSignIn({
  enabled,
  setBusy,
  setError,
  signUpAccess = () => ({}),
  onSignUpRefused,
}: {
  /** False once somebody is signed in: there is nothing to offer and nothing to fetch. */
  enabled: boolean;
  setBusy: (busy: boolean) => void;
  /** The host's one error slot; `null` clears it. */
  setError: (message: string | null) => void;
  /** GH-871b: the invite code or join token this sign-in carries, read at the moment it is sent. */
  signUpAccess?: () => SignUpAccess;
  /**
   * GH-871b: a new person without a usable invite (`invite_required` / `invite_unavailable`). The
   * host keeps them on the sheet with the code field; `attempt` lets it send the same
   * confirmation again — the nonce was not spent. Absent: the refusal is an ordinary error.
   */
  onSignUpRefused?: (attempt: DoorAttempt, refusal: DonorApiError) => void;
}): ProviderSignIn {
  // The server's own sentence where it gave one; otherwise the door's plain fallback.
  const fail = (caught: unknown, fallback: string) =>
    setError(caught instanceof DonorApiError ? caught.message : fallback);

  const [options, setOptions] = useState<DonorSignInOptions | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    // A failed read just leaves the phone, which is always the door.
    fetchSignInOptions()
      .then((value) => {
        if (!cancelled) setOptions(value);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const appleReturn = appleReturnAddress(options);
  const googleReturn = googleReturnAddress(options);
  /** The Google attempt in flight; a second tap supersedes it rather than queueing behind it. */
  const googleAttempt = useRef<AbortController | null>(null);
  useEffect(() => () => googleAttempt.current?.abort(), []);

  const signInWithApple = async (onIssued: IssuedHandler) => {
    const clientId = options?.apple?.clientId;
    if (!clientId || !appleReturn) return;
    setError(null);
    setBusy(true);
    let attempt: DoorAttempt | null = null;
    try {
      const { nonce } = await startAppleWebSignIn();
      const authorized = await authorizeWithApple({ clientId, redirectUri: appleReturn, nonce });
      attempt = {
        door: "apple",
        credential: authorized.idToken,
        nonce,
        ...(authorized.displayName ? { displayName: authorized.displayName } : {}),
      };
      const issued = await confirmAppleWebSignIn({
        credential: attempt.credential,
        nonce,
        ...(authorized.displayName ? { displayName: authorized.displayName } : {}),
        ...accessFields(signUpAccess()),
      });
      rememberToken(issued.token);
      await onIssued(issued);
    } catch (caught) {
      if (attempt && onSignUpRefused && isSignUpRefusal(caught)) {
        onSignUpRefused(attempt, caught);
        return;
      }
      // Closing Apple's sheet is a choice, not an error; anything else gets one plain sentence.
      const cancelled =
        typeof caught === "object" && caught !== null && "error" in caught
          ? String((caught as { error: unknown }).error).includes("popup_closed")
          : false;
      if (!cancelled) fail(caught, "Apple sign-in didn't finish. Try again or use your phone.");
    } finally {
      setBusy(false);
    }
  };

  /*
    The window opens first, synchronously inside the tap — a window opened after an `await` is a
    popup the browser blocks. The buttons stay live while Google's page is up: a donor who closes
    it simply taps again, which supersedes this attempt.
  */
  const signInWithGoogle = async (onIssued: IssuedHandler) => {
    const clientId = options?.google?.clientId;
    if (!clientId || !googleReturn) return;
    googleAttempt.current?.abort();
    const attempt = new AbortController();
    googleAttempt.current = attempt;
    const popup = openGoogleWindow();
    setError(null);
    if (!popup) {
      setError("Allow pop-ups to continue with Google, or use your phone.");
      return;
    }
    let refused: DoorAttempt | null = null;
    try {
      const { nonce } = await startGoogleWebSignIn();
      const idToken = await authorizeWithGoogle({
        popup,
        clientId,
        redirectUri: googleReturn,
        nonce,
        signal: attempt.signal,
      });
      setBusy(true);
      refused = { door: "google", credential: idToken, nonce };
      const issued = await confirmGoogleWebSignIn({
        credential: idToken,
        nonce,
        ...accessFields(signUpAccess()),
      });
      rememberToken(issued.token);
      await onIssued(issued);
    } catch (caught) {
      if (refused && onSignUpRefused && isSignUpRefusal(caught)) {
        onSignUpRefused(refused, caught);
        return;
      }
      // Declining on Google's page, or tapping again, is a choice, not an error.
      if (!(caught instanceof GoogleSignInCancelled)) {
        popup.close();
        fail(caught, "Google sign-in didn't finish. Try again or use your phone.");
      }
    } finally {
      if (googleAttempt.current === attempt) googleAttempt.current = null;
      setBusy(false);
    }
  };

  return {
    appleOffered: appleReturn !== null,
    googleOffered: googleReturn !== null,
    signInWithApple,
    signInWithGoogle,
  };
}
